import {
  ColumnWidthOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  ExportOutlined,
  FolderOutlined,
  ShareAltOutlined,
  TagsOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import axios from "axios";
import { baseURL } from "../../../../../../constant";
import {
  useBulkDownloadFilesMutation,
  useDeleteFileMutation,
  useGetFilesQuery,
  useShareRecordFilesMutation,
} from "../../../../../../store/apiSlice/fileApiSlice";
import { useGetCurrentViewDetailsQuery } from "../../../../../../store/apiSlice/newInterfaceApiSlice";
import { PO_TABLE_ROW_BG_ALT } from "../../../../../shared/orderLineForm/components/addItemsPickerStyles";
import { Button } from "../../Button/Button";
import { showUndoToast } from "../../../../../../helper/undoToast";
import { FileCategories } from "./FileCategories";
import { UploadFiles } from "./UploadFiles";
import { EditColumn } from "../EditColumn/EditColumn";
import { headerForConsignmentFiles, rowDataOfFileTable } from "../constantForFile";
import { useNotification } from "../../../../../../hooks/useNotification";

const FILES_QUERY_LIMIT = 100;

export const RECORD_FILES_CONFIG = {
  shipment: {
    mode: "consignment",
    idParam: "consignment_id_in_module",
    uploadComponentId: "consignmentDetailFile",
    shareParentType: "consignment",
    documentContextType: "shipment",
  },
  purchase: {
    mode: "purchase_order",
    idParam: "po_id_in_module",
    uploadComponentId: "purchaseDetailFile",
    shareParentType: "purchase_order",
    documentContextType: "po",
  },
  sales: {
    mode: "sale_order",
    idParam: "so_id_in_module",
    uploadComponentId: "salesOrderDetailFile",
    shareParentType: "sale_order",
    documentContextType: "sale_order",
  },
};
const ShareDocumentDrawer = lazy(() =>
  import("../../../../../../components/organisms/documentShare/ShareDocumentDrawer").then(
    module => ({ default: module.ShareDocumentDrawer }),
  ),
);

export function RecordFilesManager({
  orderId,
  orderName,
  orderType = "shipment",
  onFileCountChange,
}) {
  const filesConfig = RECORD_FILES_CONFIG[orderType] ?? RECORD_FILES_CONFIG.shipment;
  const queryParams = new URLSearchParams({
    limit: FILES_QUERY_LIMIT,
    offset: 0,
    mode: filesConfig.mode,
  });
  queryParams.set(filesConfig.idParam, orderId);
  const query = queryParams.toString();
  const { data, isLoading, refetch } = useGetFilesQuery(query);
  const [deleteFile] = useDeleteFileMutation();
  const [shareRecordFiles] = useShareRecordFilesMutation();
  const [bulkDownloadFiles] = useBulkDownloadFilesMutation();
  const notify = useNotification();
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const pendingDeletes = useRef(new Set());
  const files = useMemo(
    () => (data?.results || []).filter(file => !pendingDeleteIds.includes(file.id)),
    [data?.results, pendingDeleteIds],
  );

  useEffect(() => {
    if (isLoading && !data) return;
    onFileCountChange?.(data?.count ?? files.length);
  }, [data, files.length, isLoading, onFileCountChange]);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadDetail, setUploadDetail] = useState(null);
  const [categoryModal, setCategoryModal] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [editColumnsOpen, setEditColumnsOpen] = useState(false);
  const emptyColumnRef = useRef(null);

  useGetCurrentViewDetailsQuery();
  const { consignment_file: currentColumnOrder } = useSelector(
    state => state.newInterface.detailsPage.columnForDetails,
  );
  const [headerList, setHeaderList] = useState(() =>
    headerForConsignmentFiles.map(columnName => ({ columnName, checked: true })),
  );

  useEffect(() => {
    if (!currentColumnOrder) return;
    setHeaderList([
      ...currentColumnOrder.map(columnName => ({ columnName, checked: true })),
      ...headerForConsignmentFiles
        .filter(columnName => !currentColumnOrder.includes(columnName))
        .map(columnName => ({ columnName, checked: false })),
    ]);
  }, [currentColumnOrder]);

  const visibleColumns = useMemo(
    () => headerList.filter(column => column.checked).map(column => column.columnName),
    [headerList],
  );
  const resolveFileUrl = url => {
    if (!url) return "";
    if (url.startsWith(baseURL) || url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }
    return `${baseURL}${url}`;
  };

  const getFileUrl = file => resolveFileUrl(file?.file_url);

  const openFilesInNewWindow = filesToOpen => {
    filesToOpen.forEach(file => {
      const url = getFileUrl(file);
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    });
  };

  const selectFile = (file, checked) => {
    setSelectedFiles(current =>
      checked
        ? current.some(item => item.id === file.id)
          ? current
          : [...current, file]
        : current.filter(item => item.id !== file.id),
    );
  };

  const downloadFileName = (selected, fallbackName) => {
    const base = (selected?.file_name || fallbackName || "download").trim();
    const extension = selected?.file_detail?.extension || "";
    if (extension && !base.toLowerCase().endsWith(extension.toLowerCase())) {
      return `${base}${extension}`;
    }
    return base;
  };

  const triggerBlobDownload = (blob, fileName) => {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(objectUrl);
  };

  const handleDownload = async () => {
    try {
      const result = await bulkDownloadFiles(selectedFiles.map(file => file.id)).unwrap();
      if (result.mode === "zip") {
        triggerBlobDownload(result.blob, "files.zip");
        return;
      }
      for (let index = 0; index < result.files.length; index += 1) {
        const file = result.files[index];
        const selected = selectedFiles[index];
        const url =
          file.url.startsWith(baseURL) ||
          file.url.startsWith("http://") ||
          file.url.startsWith("https://")
            ? file.url
            : `${baseURL}${file.url}`;
        const response = await axios.get(url, { responseType: "blob" });
        const blob =
          response.data instanceof Blob
            ? response.data
            : new Blob([response.data], {
                type: response.headers["content-type"] || "application/octet-stream",
              });
        triggerBlobDownload(blob, downloadFileName(selected, file.name));
      }
    } catch (error) {
      notify({
        type: "error",
        message: error?.data?.detail || "Unable to download the selected files.",
      });
    }
  };

  const handleDelete = () => {
    const ids = selectedFiles.map(file => file.id);
    const count = ids.length;
    if (!count) return;
    const unhide = () => setPendingDeleteIds(previous => previous.filter(id => !ids.includes(id)));
    setPendingDeleteIds(previous => [...previous, ...ids]);
    setSelectedFiles([]);

    const handle = showUndoToast({
      message: `${count} ${count === 1 ? "file" : "files"} deleted, including from related orders`,
      onUndo: () => {
        pendingDeletes.current.delete(handle);
        unhide();
      },
      onCommit: async () => {
        pendingDeletes.current.delete(handle);
        try {
          for (const id of ids) await deleteFile({ id }).unwrap();
          refetch();
        } catch (error) {
          unhide();
          refetch();
          notify({
            type: "error",
            message: error?.data?.detail || "Couldn't delete the selected files. Please try again.",
          });
        }
      },
    });
    pendingDeletes.current.add(handle);
  };

  // Deletes wait out the Undo window; send them straight away when leaving this
  // record or the page so a file never comes back unexpectedly.
  useEffect(() => {
    const flushAll = () => pendingDeletes.current.forEach(handle => handle.flush());
    window.addEventListener("beforeunload", flushAll);
    return () => {
      window.removeEventListener("beforeunload", flushAll);
      flushAll();
    };
  }, [orderId]);

  const openEdit = () => {
    const file = selectedFiles[0];
    setUploadDetail({
      category: file.document_category?.id || "",
      subCategory: file.document_sub_category?.id || "",
      reference: file.reference_no || "",
      id: file.id,
      file_name: file.file_name || file.file_detail?.name,
      selectedFiles,
    });
  };

  return (
    <ManagerLayout>
      <Toolbar aria-label="File actions">
        <ToolbarSection>
          <Button
            btnName={
              <>
                <UploadOutlined aria-hidden="true" /> Upload Files
              </>
            }
            classOfBtn="drawerAction isPrimary"
            handler={() => setUploadDetail({})}
          />
        </ToolbarSection>
        <ToolbarSection>
          <Button
            btnName={
              <>
                <ExportOutlined aria-hidden="true" /> Open
              </>
            }
            classOfBtn="drawerAction"
            isDisabledButton={selectedFiles.length === 0}
            titleForDisabledBtn="Select at least one file to open."
            handler={() => openFilesInNewWindow(selectedFiles)}
          />
          <Button
            btnName={
              <>
                <EditOutlined aria-hidden="true" /> Edit
              </>
            }
            classOfBtn="drawerAction"
            isDisabledButton={selectedFiles.length !== 1}
            titleForDisabledBtn="Select one file to edit."
            handler={openEdit}
          />
          <Button
            btnName={
              <>
                <DeleteOutlined aria-hidden="true" /> Delete
              </>
            }
            classOfBtn="drawerAction isDanger"
            isDisabledButton={selectedFiles.length === 0}
            titleForDisabledBtn="Select at least one file to delete."
            handler={handleDelete}
          />
          <Button
            btnName={
              <>
                <ShareAltOutlined aria-hidden="true" /> Share
              </>
            }
            classOfBtn="drawerAction"
            isDisabledButton={selectedFiles.length === 0}
            titleForDisabledBtn="Select at least one file to share."
            handler={() => setShareOpen(true)}
          />
          <Button
            btnName={
              <>
                <DownloadOutlined aria-hidden="true" /> Download
              </>
            }
            classOfBtn="drawerAction"
            isDisabledButton={selectedFiles.length === 0}
            titleForDisabledBtn="Select at least one file. Up to 10 files and 20 MB download separately. Larger selections download as a zip."
            handler={handleDownload}
          />
        </ToolbarSection>
        <ToolbarSection>
          <Button
            btnName={
              <>
                <FolderOutlined aria-hidden="true" /> Category
              </>
            }
            classOfBtn={`drawerAction ${categoryModal === "category" ? "isActive" : ""}`}
            handler={() => setCategoryModal("category")}
          />
          <Button
            btnName={
              <>
                <TagsOutlined aria-hidden="true" /> Sub Category
              </>
            }
            classOfBtn={`drawerAction ${categoryModal === "subCategory" ? "isActive" : ""}`}
            handler={() => setCategoryModal("subCategory")}
          />
        </ToolbarSection>
        <ToolbarSection>
          <Button
            btnName={
              <>
                <ColumnWidthOutlined aria-hidden="true" /> Edit Columns
              </>
            }
            classOfBtn={`drawerAction ${editColumnsOpen ? "isActive" : ""}`}
            handler={() => setEditColumnsOpen(true)}
          />
        </ToolbarSection>
      </Toolbar>

      <TableScroller>
        <table>
          <thead>
            <tr>
              <th className="checkBox">
                <input
                  type="checkbox"
                  aria-label="Select all files"
                  checked={files.length > 0 && selectedFiles.length === files.length}
                  onChange={event => setSelectedFiles(event.target.checked ? files : [])}
                />
              </th>
              <th>#</th>
              {visibleColumns.map(column => (
                <th key={column} className={column === "NAME" ? "nameOfFile" : undefined}>
                  {column}
                </th>
              ))}
              <th ref={emptyColumnRef} />
            </tr>
          </thead>
          <tbody>
            {files.map((file, fileIndex) => (
              <tr key={file.id}>
                <td className="checkBox">
                  <input
                    type="checkbox"
                    aria-label={`Select ${file.file_name || file.file_detail?.name || "file"}`}
                    checked={selectedFiles.some(item => item.id === file.id)}
                    onChange={event => selectFile(file, event.target.checked)}
                  />
                </td>
                <td>{fileIndex + 1}</td>
                {rowDataOfFileTable(file, visibleColumns, emptyColumnRef, getFileUrl)}
                <td />
              </tr>
            ))}
          </tbody>
        </table>
        {isLoading && <EmptyMessage>Loading files…</EmptyMessage>}
        {!isLoading && files.length === 0 && <EmptyMessage>No files attached yet.</EmptyMessage>}
      </TableScroller>

      {uploadDetail && (
        <UploadFiles
          id={orderId}
          recordName={orderName}
          closeUploadFiles={() => setUploadDetail(null)}
          editUploadFileDetail={uploadDetail.id ? uploadDetail : null}
          getFiles={refetch}
          componentId={filesConfig.uploadComponentId}
        />
      )}
      {categoryModal && (
        <FileCategories
          closeCategoryModal={() => {
            setCategoryModal(null);
            refetch();
          }}
          isOpenCategoryModal={categoryModal}
        />
      )}
      {shareOpen && (
        <Suspense fallback={null}>
          <ShareDocumentDrawer
            title="Share Files"
            onClose={() => setShareOpen(false)}
            onShare={payload =>
              shareRecordFiles({
                ...payload,
                document_ids: selectedFiles.map(file => file.id),
                parent_type: filesConfig.shareParentType,
                parent_id: orderId,
              }).unwrap()
            }
            documentContext={{
              document_type: filesConfig.documentContextType,
              document_id: orderId,
            }}
          />
        </Suspense>
      )}
      {editColumnsOpen && (
        <EditColumn
          headerList={headerList}
          setHeaderList={setHeaderList}
          closeEditColumn={() => setEditColumnsOpen(false)}
          orderType="consignment_file"
          asDrawer
        />
      )}
    </ManagerLayout>
  );
}

const ManagerLayout = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
`;

const Toolbar = styled.nav`
  align-items: center;
  background: #fff;
  border-bottom: 1px solid #ecf1f4;
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  gap: 10px;
  padding: 12px 16px;

  .drawerAction {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 34px;
    padding: 0 12px;
    border: 1px solid #d5deea !important;
    border-radius: 6px;
    background: #fff !important;
    color: #1249bf !important;
    font-size: 13px;
    font-weight: 600;
    line-height: 20px;
    white-space: nowrap;
    cursor: pointer;
    transition:
      background-color 0.15s ease,
      border-color 0.15s ease,
      color 0.15s ease;
  }

  .drawerAction .anticon {
    font-size: 14px;
  }

  .drawerAction:hover:not(:disabled) {
    background: #f4f7ff !important;
    border-color: #2060ea !important;
    color: #1249bf !important;
  }

  .drawerAction.isActive {
    background: #e8f0ff !important;
    border-color: #2060ea !important;
    color: #1249bf !important;
  }

  .drawerAction.isPrimary {
    background: #2060ea !important;
    border-color: #2060ea !important;
    color: #fff !important;
  }

  .drawerAction.isPrimary:hover:not(:disabled) {
    background: #1249bf !important;
    border-color: #1249bf !important;
    color: #fff !important;
  }

  .drawerAction.isDanger:hover:not(:disabled) {
    background: #fff5f6 !important;
    border-color: #cf324a !important;
    color: #cf324a !important;
  }

  .drawerAction:disabled {
    background: #f7f8f9 !important;
    border-color: #e5eaf0 !important;
    color: rgba(0, 0, 0, 0.25) !important;
    cursor: not-allowed;
  }
`;

const ToolbarSection = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding-left: 12px;
  border-left: 1px solid #e5eaf0;

  &:first-child {
    padding-left: 0;
    border-left: none;
  }
`;

const TableScroller = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;

  table {
    border-collapse: collapse;
    min-width: 100%;
    white-space: nowrap;
    width: max-content;
  }

  th,
  td {
    border-bottom: 1px solid #e5eaf0;
    color: #10244d;
    font-size: 13px;
    padding: 12px 14px;
    text-align: left;
  }

  th {
    background: #fff;
    font-size: 11px;
    font-weight: 600;
    position: sticky;
    text-transform: uppercase;
    top: 0;
    z-index: 1;
  }

  tbody tr:nth-child(odd) > td {
    background: #fff;
  }
  tbody tr:nth-child(even) > td {
    background: ${PO_TABLE_ROW_BG_ALT};
  }

  .checkBox {
    padding-left: 16px;
    padding-right: 4px;
    width: 36px;
  }

  .nameOfFile {
    max-width: 240px;
  }

  .fileNameOpenLink {
    color: #1249bf;
    max-width: 100%;
    text-decoration: none;
  }

  .fileNameOpenLink:hover {
    text-decoration: underline;
  }
`;

const EmptyMessage = styled.p`
  color: #4a4a68;
  margin: 28px 16px;
`;
